import clsx from "clsx";
import type { ReactNode } from "react";
import type { View } from "../../../../common/types.ts";
import { PlayoffMatchup } from "../components/PlayoffMatchup.tsx";
import useTitleBar from "../hooks/useTitleBar.tsx";
import {
  MobileActionableEmptyState,
  mobileActionButtonClassNames,
  MobileContentHero,
  MobileExpertDisclosure,
  MobileStatRow,
} from "../mobile/mobilePrimitives.tsx";
import { helpers } from "../util/helpers.ts";
import { useLocalPartial } from "../util/local.ts";
import { createMobileCrossTabHref } from "../util/mobileCrossTabLink.ts";
import i18n from "../util/i18n.ts";
import { parseMobileReturnState } from "../util/mobilePageContext.ts";
import { translateTeamRegion } from "../util/teamNames.ts";
import {
  findUserPlayoffSeries,
  getPlayoffPathContext,
  getPlayoffRoundStatus,
  getPlayoffSummaryTone,
  isHistoricalPlayoffsSeason,
  type PlayoffRoundStatus,
} from "./gamesTemplate.ts";

const roundTitle = (roundIndex: number, numRounds: number) => {
  if (roundIndex === numRounds - 1) {
    return i18n.t("playoffs.finals", {
      ns: "ui",
      defaultValue: "Finals",
    });
  }

  return i18n.t("playoffs.roundNumber", {
    ns: "ui",
    round: roundIndex + 1,
    defaultValue: "Round {{round}}",
  });
};

// N3-B: per-round status pill (completed / in progress / not started). Called at
// render time so the i18n lookups never run at module top level.
const RoundStatusBadge = ({ status }: { status: PlayoffRoundStatus }) => {
  const badges = {
    completed: {
      className: "text-bg-success",
      label: i18n.t("playoffs.roundStatusCompleted", {
        ns: "ui",
        defaultValue: "Completed",
      }),
    },
    inProgress: {
      className: "text-bg-primary",
      label: i18n.t("playoffs.roundStatusInProgress", {
        ns: "ui",
        defaultValue: "In progress",
      }),
    },
    notStarted: {
      className: "text-bg-secondary",
      label: i18n.t("playoffs.roundStatusNotStarted", {
        ns: "ui",
        defaultValue: "Not started",
      }),
    },
  } as const;
  const badge = badges[status];

  return (
    <span
      className={clsx("badge", "flex-shrink-0", badge.className)}
      data-mobile-playoff-round-status={status}
    >
      {badge.label}
    </span>
  );
};

const RoundGroupHeader = ({
  status,
  title,
}: {
  status: PlayoffRoundStatus;
  title: ReactNode;
}) => (
  <div className="d-flex align-items-center justify-content-between gap-2 mb-2">
    <h2 className="h5 mb-0">{title}</h2>
    <RoundStatusBadge status={status} />
  </div>
);

const SeriesCard = ({
  advancement,
  decided,
  footer,
  mobileRole,
  numGamesToWinSeries,
  season,
  series,
  userTid,
}: {
  advancement?: ReactNode;
  decided?: boolean;
  footer?: string;
  mobileRole?: "round" | "user";
  numGamesToWinSeries: number;
  season: number;
  series: View<"playoffs">["series"][number][number];
  userTid: number;
}) => {
  const isUser = mobileRole === "user";

  return (
    <article
      // N3-B: the user's own series carries a persistent left color band so the
      // player can track "my path" at a glance across every round, and an id so
      // the top summary bar can scroll straight to it.
      className={clsx("card", {
        "border-start border-4 border-info": isUser,
      })}
      data-mobile-playoff-series={mobileRole ?? "round"}
      data-mobile-playoff-decided={decided ? "true" : "false"}
      {...(isUser
        ? { "data-mobile-playoff-user-path": "", id: "playoffs-user-series" }
        : {})}
    >
      <div className="card-body">
        <PlayoffMatchup
          expandTeamNames
          // A decided series highlights the winning team's row (existing
          // win/loss style system) so the advancing team pops.
          extraHighlight={decided}
          numGamesToWinSeries={numGamesToWinSeries}
          season={season}
          series={series}
          userTid={userTid}
        />
        {advancement ? (
          <div
            className="d-flex align-items-center gap-1 small text-body-secondary mt-2"
            data-mobile-playoff-advancement=""
          >
            <span aria-hidden="true">&rarr;</span>
            <span>{advancement}</span>
          </div>
        ) : null}
        {footer ? (
          <div className="text-body-secondary small mt-2">{footer}</div>
        ) : null}
      </div>
    </article>
  );
};

const getSeriesWinner = (
  series: View<"playoffs">["series"][number][number] | undefined,
  winsNeeded: number | undefined,
) => {
  if (!series || winsNeeded === undefined) {
    return;
  }

  if ((series.home.won ?? 0) >= winsNeeded) {
    return series.home;
  }

  if (series.away && (series.away.won ?? 0) >= winsNeeded) {
    return series.away;
  }
};

const getPlayoffTeamLabel = (team: ReturnType<typeof getSeriesWinner>) => {
  if (!team) {
    return "";
  }

  if (team.region) {
    return translateTeamRegion(team.region);
  }

  return i18n.t("common.unavailable", {
    ns: "ui",
    defaultValue: "Unavailable",
  });
};

const Playoffs = ({
  finalMatchups,
  numGamesPlayoffSeries,
  numGamesToWinSeries,
  playIns,
  season,
  series,
  userTid,
}: View<"playoffs">) => {
  useTitleBar({
    title: i18n.t("playoffs.title", { ns: "ui", defaultValue: "Playoffs" }),
    jumpTo: true,
    jumpToSeason: season,
    dropdownView: "playoffs",
    dropdownFields: {
      seasons: season,
    },
  });

  const { season: currentSeason } = useLocalPartial(["season"]);
  const numRounds = series.length;
  const isHistoricalSeason = isHistoricalPlayoffsSeason({
    currentSeason,
    viewSeason: season,
  });
  const mobileReturnState =
    typeof window !== "undefined" ? parseMobileReturnState(window.location.search) : {};
  const currentPlayoffsHref = helpers.leagueUrl(["playoffs"]);
  const playoffsArchiveReturnHref =
    mobileReturnState.returnHref ?? mobileReturnState.returnUrl ?? currentPlayoffsHref;
  const champion = getSeriesWinner(
    series.at(-1)?.at(0),
    numGamesToWinSeries.at(-1),
  );
  const championLabel = getPlayoffTeamLabel(champion);
  const seasonReviewTitle = i18n.t("playoffs.p2s05SeasonReviewTitle", {
    ns: "ui",
    season,
    defaultValue: "{{season}} playoffs review",
  });

  // Historical / archive return banner. This is a functional control (return to
  // current playoffs), not narration, so it stays; the P0-4 return-stack fields
  // and P0-7 readonly-archive markers are load-bearing across pages.
  const seasonContext = (
    <section
      className="alert alert-light border py-2 mb-3"
      data-mobile-playoffs-archive=""
    >
      <div className="d-flex flex-wrap align-items-start justify-content-between gap-2">
        <div>
          <div className="fw-semibold">
            {i18n.t("playoffs.p0s07ArchiveReadonlyTitle", {
              ns: "ui",
              season,
              defaultValue: "{{season}} 季后赛档案",
            })}
          </div>
          <div className="small text-body-secondary">
            {i18n.t("playoffs.p0s07ArchiveReadonlyBody", {
              ns: "ui",
              defaultValue: "回顾这个赛季的季后赛对阵图。",
            })}
          </div>
        </div>
        <a
          aria-label={i18n.t("playoffs.p2s02ReturnCurrentPlayoffs", {
            ns: "ui",
            defaultValue: "回到当前季后赛",
          })}
          className="btn btn-outline-secondary btn-sm"
          href={playoffsArchiveReturnHref}
        >
          {i18n.t("playoffs.p0s07ReturnCurrentPlayoffs", {
            ns: "ui",
            defaultValue: "回到当前季后赛",
          })}
        </a>
      </div>
      <div className="d-flex flex-wrap gap-2 mt-2">
        <span className="badge text-bg-light border">
          {i18n.t("playoffs.seasonContext", {
            ns: "ui",
            season,
            defaultValue: "{{season}} 赛季",
          })}
        </span>
        <span className="badge text-bg-secondary">
          {i18n.t("playoffs.p2s05CurrentStageHidden", {
            ns: "ui",
            defaultValue: "只能查看",
          })}
        </span>
      </div>
      <MobileExpertDisclosure
        className="mt-2"
        expertHostId="playoffs-archive-matchday"
        summary={i18n.t("playoffs.p0s07ArchiveContextDepth", {
          ns: "ui",
          defaultValue: "历史比赛日",
        })}
      >
        <a
          className="btn btn-link btn-sm px-0"
          href={helpers.leagueUrl(["daily_schedule", season])}
        >
          {i18n.t("playoffs.openSeasonMatchday", {
            ns: "ui",
            defaultValue: "打开该赛季比赛日",
          })}
        </a>
      </MobileExpertDisclosure>
    </section>
  );

  if (numRounds === 0) {
    return (
      <div
        data-desktop-production-view="playoffs"
      >
        {isHistoricalSeason ? seasonContext : null}
        <MobileContentHero
          data-v5-playoffs-bracket-first-screen=""
          data-v5-playoffs-content-first-screen=""
          title={seasonReviewTitle}
        >
          <MobileActionableEmptyState
            action={
              <a
                className={mobileActionButtonClassNames.secondary}
                data-v5-playoffs-empty-action=""
                href={createMobileCrossTabHref(helpers.leagueUrl(["standings", season]))}
              >
                {i18n.t("playoffs.reviewSeasonStandings", {
                  ns: "ui",
                  defaultValue: "Review season standings",
                })}
              </a>
            }
            data-v5-playoffs-empty-state=""
            impact={i18n.t("playoffs.noSeriesImpact", {
              ns: "ui",
              defaultValue:
                "The regular-season standings decide the final result for this season.",
            })}
            reason="empty"
            reasonText={
              finalMatchups
                ? i18n.t("playoffs.noPlayoffs", {
                    ns: "ui",
                    defaultValue: "There were no playoffs this season.",
                  })
                : i18n.t("playoffs.noPlayoffSeries", {
                    ns: "ui",
                    defaultValue:
                      "Your league is configured to not have any playoff series. The league champion will be the 1st place team in the regular season.",
                  })
            }
            title={i18n.t("playoffs.noPlayoffTitle", {
              ns: "ui",
              defaultValue: "No playoff bracket",
            })}
          />
        </MobileContentHero>
      </div>
    );
  }

  const showFooter = numGamesPlayoffSeries.some(
    (numGames) => numGames !== numGamesPlayoffSeries[0],
  );
  const userSeries = findUserPlayoffSeries({ series, userTid });

  const bestOfFooter = (roundIndex: number) =>
    showFooter
      ? i18n.t("playoffs.bestOf", {
          ns: "ui",
          games: numGamesPlayoffSeries[roundIndex],
          defaultValue: "Best of {{games}}",
        })
      : undefined;

  // Height budget (G4-1 metrics): inlining every bracket round pushed the page
  // past the 4-screen gate. Keep the live rounds expanded — the user's own
  // round, the current/in-progress round, and the finals — and fold the
  // already-decided earlier rounds behind one expert disclosure. Archive / a
  // finished season (no live round) then shows only the finals + champion, with
  // every earlier round folded. No bracket content is removed, only collapsed.
  const roundIsComplete = (roundIndex: number) => {
    const round = series[roundIndex];
    return (
      !!round &&
      round.length > 0 &&
      round.every(
        (seriesInfo) =>
          getSeriesWinner(seriesInfo, numGamesToWinSeries[roundIndex]) !==
          undefined,
      )
    );
  };
  const finalsRoundIndex = numRounds - 1;
  // Playoffs resolve in order, so the earliest still-undecided round is where
  // live play sits. -1 (every round decided) means there is no live round.
  const currentRoundIndex = series.findIndex(
    (_round, roundIndex) => !roundIsComplete(roundIndex),
  );
  // A round stays expanded when it is the user's own round, the live round, or
  // the finals; a not-yet-drawn round is always shown inline (its TBD
  // placeholder is cheap and marks the path ahead). Only already-decided,
  // out-of-focus rounds fold, so height stays close to the pre-N3-B page.
  const isRoundFocused = (roundIndex: number) =>
    roundIndex === finalsRoundIndex ||
    roundIndex === currentRoundIndex ||
    roundIndex === userSeries?.roundIndex;

  // N3-B: the top summary bar reads the user's live path status (still in / out
  // / never qualified) from the shared helper, then scrolls to their own card.
  const pathContext = getPlayoffPathContext({
    finalMatchups,
    numGamesToWinSeries,
    numRounds,
    userSeries,
    userTid,
  });
  const userSeriesData = userSeries?.series;
  const opponentTeam = userSeriesData
    ? userSeriesData.home?.tid === userTid
      ? userSeriesData.away
      : userSeriesData.home
    : undefined;
  const opponentLabel = getPlayoffTeamLabel(opponentTeam);
  const userIsChampion = champion !== undefined && champion.tid === userTid;
  const summaryTone = pathContext.hasUserSeries
    ? getPlayoffSummaryTone(pathContext.status)
    : "notQualified";
  const summaryScore =
    pathContext.hasUserSeries && summaryTone !== "notQualified"
      ? `${pathContext.userWins}-${pathContext.opponentWins}`
      : undefined;

  let summaryMessage: string;
  if (summaryTone === "notQualified") {
    summaryMessage = finalMatchups
      ? i18n.t("playoffs.pathNotQualified", {
          ns: "ui",
          defaultValue: "Did not make the playoffs",
        })
      : i18n.t("playoffs.pathNotProjected", {
          ns: "ui",
          defaultValue: "Not in the projected bracket",
        });
  } else if (summaryTone === "eliminated") {
    summaryMessage = i18n.t("playoffs.pathEliminated", {
      ns: "ui",
      round: roundTitle(pathContext.roundIndex!, numRounds),
      defaultValue: "Eliminated in {{round}}",
    });
  } else if (userIsChampion) {
    summaryMessage = i18n.t("playoffs.pathChampion", {
      ns: "ui",
      defaultValue: "Won the championship",
    });
  } else {
    summaryMessage = i18n.t(
      finalMatchups ? "playoffs.pathActive" : "playoffs.pathProjected",
      {
        ns: "ui",
        round: roundTitle(pathContext.roundIndex!, numRounds),
        opponent: opponentLabel,
        defaultValue: finalMatchups
          ? "{{round}} vs {{opponent}}"
          : "Projected: {{round}} vs {{opponent}}",
      },
    );
  }

  const summaryBandClass = {
    active: "border-success",
    eliminated: "border-danger",
    notQualified: "border-secondary",
  }[summaryTone];
  const summaryHref = pathContext.hasUserSeries
    ? "#playoffs-user-series"
    : undefined;
  const summaryInner = (
    <div className="card-body py-2 d-flex align-items-center justify-content-between gap-3">
      <div className="min-w-0">
        <div className="small text-body-secondary">
          {i18n.t("playoffs.pathSummaryTitle", {
            ns: "ui",
            defaultValue: "Your playoff path",
          })}
        </div>
        <div className="fw-semibold">{summaryMessage}</div>
      </div>
      {summaryScore ? (
        <div className="fs-5 fw-bold flex-shrink-0">{summaryScore}</div>
      ) : null}
    </div>
  );
  const summaryBar = summaryHref ? (
    <a
      className={clsx(
        "card mb-3 d-block text-decoration-none text-reset border-start border-4",
        summaryBandClass,
      )}
      data-mobile-playoffs-summary=""
      data-mobile-playoffs-summary-tone={summaryTone}
      href={summaryHref}
    >
      {summaryInner}
    </a>
  ) : (
    <div
      className={clsx("card mb-3 border-start border-4", summaryBandClass)}
      data-mobile-playoffs-summary=""
      data-mobile-playoffs-summary-tone={summaryTone}
    >
      {summaryInner}
    </div>
  );

  const renderRoundGroup = (
    round: View<"playoffs">["series"][number],
    roundIndex: number,
  ) => {
    const winsNeeded = numGamesToWinSeries[roundIndex];
    const status = getPlayoffRoundStatus({ round, winsNeeded });
    const nextRoundTitle =
      roundIndex < finalsRoundIndex
        ? roundTitle(roundIndex + 1, numRounds)
        : undefined;

    return (
      <section key={roundIndex} data-mobile-playoffs-round="">
        <RoundGroupHeader
          status={status}
          title={roundTitle(roundIndex, numRounds)}
        />
        <div className="d-flex flex-column gap-2">
          {status === "notStarted" ? (
            <article
              className="card"
              data-mobile-playoff-series="placeholder"
              data-mobile-playoff-round-tbd=""
            >
              <div className="card-body text-body-secondary small">
                {i18n.t("playoffs.matchupTbd", {
                  ns: "ui",
                  defaultValue: "Matchup to be determined",
                })}
              </div>
            </article>
          ) : (
            round.map((seriesInfo, seriesIndex) => {
              const winner = getSeriesWinner(seriesInfo, winsNeeded);
              const decided = winner !== undefined;
              const isUser =
                userSeries?.roundIndex === roundIndex &&
                userSeries.seriesIndex === seriesIndex;
              const advancement =
                decided && nextRoundTitle
                  ? i18n.t("playoffs.advancesTo", {
                      ns: "ui",
                      team: getPlayoffTeamLabel(winner),
                      round: nextRoundTitle,
                      defaultValue: "{{team}} advances to {{round}}",
                    })
                  : undefined;

              return (
                <SeriesCard
                  advancement={advancement}
                  decided={decided}
                  footer={bestOfFooter(roundIndex)}
                  key={seriesIndex}
                  mobileRole={isUser ? "user" : "round"}
                  numGamesToWinSeries={winsNeeded!}
                  season={season}
                  series={seriesInfo}
                  userTid={userTid}
                />
              );
            })
          )}
        </div>
      </section>
    );
  };

  // Play-in feeds round 1, so once the first round has any result the play-in is
  // history and folds with the other decided rounds; before then it is the live
  // entry stage and stays expanded.
  const round0Started =
    series[0]?.some(
      (seriesInfo) =>
        (seriesInfo.home?.won ?? 0) > 0 || (seriesInfo.away?.won ?? 0) > 0,
    ) ?? false;
  const playInIsHistory = round0Started || roundIsComplete(0);

  const renderPlayInGroup = () => {
    if (!playIns) {
      return null;
    }

    return (
      <section data-mobile-playoffs-round="" data-v5-playoffs-play-in="">
        <RoundGroupHeader
          status={playInIsHistory ? "completed" : "inProgress"}
          title={i18n.t("playoffs.playInTournament", {
            ns: "ui",
            defaultValue: "Play-In Tournament",
          })}
        />
        <p className="mb-2 small text-body-secondary">
          {i18n.t(
            playIns.length === 1
              ? "playoffs.playInDescSingle"
              : "playoffs.playInDescMulti",
            {
              ns: "ui",
              defaultValue:
                playIns.length === 1
                  ? "The winner of the {{seedA}} game makes the playoffs. Then the loser plays the winner of the {{seedB}} game for the final playoffs spot."
                  : "The winners of the {{seedA}} games make the playoffs. Then the losers play the winners of the {{seedB}} games for the final playoffs spots.",
              seedA: `${playIns[0]![0].home.seed}/${playIns[0]![0].away.seed}`,
              seedB: `${playIns[0]![1].home.seed}/${playIns[0]![1].away.seed}`,
            },
          )}
        </p>
        <div className="d-flex flex-column gap-2">
          {[...playIns].reverse().flatMap((playIn, i) =>
            playIn.map((seriesInfo, j) => (
              <SeriesCard
                decided={getSeriesWinner(seriesInfo, 1) !== undefined}
                key={`${i}-${j}`}
                numGamesToWinSeries={1}
                season={season}
                series={seriesInfo}
                userTid={userTid}
              />
            )),
          )}
        </div>
      </section>
    );
  };

  // Assemble every group in chronological order — play-in first, then round 1
  // through the finals — and split into folded (decided, out-of-focus) and
  // inline (focused / live / not-yet-drawn) buckets while preserving that order.
  const collapsedGroups: ReactNode[] = [];
  const expandedGroups: ReactNode[] = [];

  const playInGroup = renderPlayInGroup();
  if (playInGroup) {
    (playInIsHistory ? collapsedGroups : expandedGroups).push(playInGroup);
  }

  series.forEach((round, roundIndex) => {
    const group = renderRoundGroup(round, roundIndex);
    const status = getPlayoffRoundStatus({
      round,
      winsNeeded: numGamesToWinSeries[roundIndex],
    });
    if (status === "completed" && !isRoundFocused(roundIndex)) {
      collapsedGroups.push(group);
    } else {
      expandedGroups.push(group);
    }
  });

  return (
    <div
      data-desktop-production-view="playoffs"
    >
      {isHistoricalSeason ? seasonContext : null}
      <MobileContentHero
        data-v5-playoffs-bracket-first-screen=""
        data-v5-playoffs-content-first-screen=""
        explainer={
          !finalMatchups
            ? i18n.t("playoffs.projectedMessage", {
                ns: "ui",
                defaultValue:
                  "This is what the playoff matchups would be if the season ended right now.",
              })
            : undefined
        }
        title={seasonReviewTitle}
      >
        {champion ? (
          <MobileStatRow
            data-v5-playoffs-champion=""
            label={i18n.t("playoffs.championLabel", {
              ns: "ui",
              defaultValue: "Champion",
            })}
            tone="positive"
            value={championLabel}
          />
        ) : null}

        {summaryBar}

        <div
          className="d-flex flex-column gap-3"
          data-mobile-playoffs-rounds
          data-v5-playoffs-bracket-rounds=""
          id="playoffs-bracket"
        >
          {collapsedGroups.length > 0 ? (
            <MobileExpertDisclosure
              count={i18n.t("playoffs.completedRoundsCount", {
                ns: "ui",
                count: collapsedGroups.length,
                defaultValue: "{{count}} rounds",
              })}
              data-mobile-playoffs-completed-rounds=""
              data-v5-playoffs-completed-rounds=""
              expertHostId="playoffs-earlier-rounds"
              summary={i18n.t("playoffs.completedRoundsSummary", {
                ns: "ui",
                defaultValue: "Earlier rounds",
              })}
            >
              <div className="d-flex flex-column gap-3">{collapsedGroups}</div>
            </MobileExpertDisclosure>
          ) : null}
          {expandedGroups}
        </div>
      </MobileContentHero>
    </div>
  );
};

export default Playoffs;
